import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import SEO from '@/components/feature/SEO';
import { mangaList } from '@/mocks/manga';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <SEO
          title={`漫画未找到 | ${t('site.name')}`}
          description="抱歉，该漫画不存在或已被移除"
        />
        <main>
          <Navbar />
          <div className="pt-20 min-h-[60vh] flex items-center justify-center px-4">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
              <Link
                to="/category/all"
                className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('article.backToList')}
              </Link>
            </div>
          </div>
          <Footer />
          <ScrollTopButton />
        </main>
      </>
    );
  }

  const relatedManga = mangaList.filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((tag) => manga.tags.includes(tag))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: manga.title,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2026-01-01',
    dateModified: manga.updateDate,
    description: manga.description,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('article.breadcrumb.home'), item: 'https://www.benziwz.com/' },
      { '@type': 'ListItem', position: 2, name: t('category.title'), item: 'https://www.benziwz.com/category/all' },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${manga.title} - ${manga.author} | ${t('site.name')}`}
        description={`${manga.description.substring(0, 120)}... - 本子网站正版高清漫画在线阅读`}
        keywords={`${manga.title},${manga.author},${manga.tags.join(',')},本子网站`}
        canonical={`https://www.benziwz.com/article/${manga.id}`}
        ogTitle={`${manga.title} - ${manga.author}`}
        ogDescription={manga.description}
        ogType="article"
        ogUrl={`https://www.benziwz.com/article/${manga.id}`}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      <main>
        <Navbar />

        <div className="bg-foreground-950 pt-20 pb-10 px-4 md:px-6">
          <div className="max-w-screen-xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('category.title'), path: '/category/all' },
                { label: manga.title },
              ]}
              dark={true}
            />
            <div className="mt-6 flex items-start gap-6">
              <div className="w-[140px] md:w-[180px] flex-shrink-0 hidden sm:block">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[5/7]">
                  <img
                    src={manga.coverImage}
                    alt={manga.title}
                    title={`${manga.title} - ${manga.author} - 本子网站`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-4xl font-heading text-background-50 mb-2">
                  {manga.title}
                </h1>
                <p className="text-sm text-background-300 mb-5">
                  {t('article.author')}: {manga.author}
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap border ${
                    manga.status === 'ongoing'
                      ? 'border-accent-400 text-accent-400'
                      : 'border-secondary-400 text-secondary-400'
                  }`}>
                    {manga.status === 'ongoing' ? t('article.status.ongoing') : t('article.status.completed')}
                  </span>
                  <span className="text-xs text-background-300/80 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </span>
                    {manga.rating} {t('article.rating')}
                  </span>
                  <span className="text-xs text-background-300/80 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-background-400 text-xs"></i>
                    </span>
                    {(manga.views / 10000).toFixed(1)}万 {t('article.views')}
                  </span>
                  <span className="text-xs text-background-300/80">
                    {manga.chapters}{t('article.chapters')}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-background-800/50 text-background-200 px-2.5 py-1 rounded-full whitespace-nowrap border border-background-700/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-background-300/70 leading-relaxed max-w-2xl">
                  {manga.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center">
                    开始阅读 - 第1话
                  </button>
                  <button className="px-6 py-2.5 bg-background-800 text-background-200 rounded-lg text-sm font-medium hover:bg-background-700 transition-colors whitespace-nowrap cursor-pointer text-center border border-background-700">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <article className="pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-10 mb-12">
              {Array.from({ length: Math.min(manga.chapters, 24) }, (_, i) => (
                <button
                  key={i + 1}
                  className="px-4 py-2.5 bg-background-100 hover:bg-background-200 rounded-lg text-sm text-foreground-700 hover:text-foreground-900 transition-colors text-left whitespace-nowrap cursor-pointer border border-background-200/60"
                >
                  第{i + 1}话
                </button>
              ))}
              {manga.chapters > 24 && (
                <button className="px-4 py-2.5 bg-background-100 hover:bg-background-200 rounded-lg text-sm text-foreground-500 transition-colors text-center whitespace-nowrap cursor-pointer border border-background-200/60">
                  查看更多...
                </button>
              )}
            </div>

            {relatedManga.length > 0 && (
              <section>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
                  {t('article.related')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedManga.map((item) => (
                    <MangaCard key={item.id} manga={item} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
        <Footer />
        <ScrollTopButton />
      </main>
    </>
  );
}